import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { closePopup } from '../../lib/popup';

import selectors from '../../selectors';
import Config from '../../constants/Config';
import DroppableTypes from '../../constants/DroppableTypes';
import { useIsMobile } from '../../hooks/use-media-query';
import ListContainer from '../../containers/ListContainer';
import CardModalContainer from '../../containers/CardModalContainer';
import ListAdd from './ListAdd';
import AgentPromptModal from '../AgentPromptModal';
import { ReactComponent as PlusMathIcon } from '../../assets/images/plus-math-icon.svg';

import styles from './Board.module.scss';
import globalStyles from '../../styles.module.scss';

const parseDndId = dndId => dndId.split(':')[1];

const Board = React.memo(
  ({ listIds, listNames, isCardModalOpened, canEdit, onListCreate, onListMove, onCardMove }) => {
    const [t] = useTranslation();
    const isMobile = useIsMobile();
    const [isListAddOpened, setIsListAddOpened] = useState(false);
    const [activeListIndex, setActiveListIndex] = useState(0);

    const accessToken = useSelector(selectors.selectAccessToken);
    const [activeListAgentId, setActiveListAgentId] = useState(null);
    const [activeListAgentName, setActiveListAgentName] = useState('');
    const [activeAgentType, setActiveAgentType] = useState('arrival');
    const [aiConfig, setAiConfig] = useState({
      systemPrompt: '',
      agentEnabled: false,
      scheduleInterval: 0,
    });
    // eslint-disable-next-line no-unused-vars
    const [isAiLoading, setIsAiLoading] = useState(false);

    const wrapper = useRef(null);
    const prevPosition = useRef(null);

    const handleAddListClick = useCallback(() => {
      setIsListAddOpened(true);
    }, []);

    const handleAddListClose = useCallback(() => {
      setIsListAddOpened(false);
    }, []);

    const handleConfigureListAgent = useCallback(
      async (listId, listName, type = 'arrival') => {
        if (!accessToken) return;
        setActiveListAgentId(listId);
        setActiveListAgentName(listName);
        setActiveAgentType(type);
        setIsAiLoading(true);

        try {
          const response = await fetch(`${Config.SERVER_BASE_URL}/api/lists/${listId}/context`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            credentials: 'include',
          });
          if (response.ok) {
            const data = await response.json();
            setAiConfig({
              systemPrompt: data.item?.aiArrivalPrompt || '',
              agentEnabled: true,
              scheduleInterval: data.item?.aiScheduleInterval || 0,
            });
          }
        } catch (error) {
          console.error('Error fetching AI config:', error);
        } finally {
          setIsAiLoading(false);
        }
      },
      [accessToken]
    );

    const handleSaveListAgentConfig = useCallback(
      async config => {
        if (!accessToken || !activeListAgentId) return;
        try {
          const response = await fetch(
            `${Config.SERVER_BASE_URL}/api/lists/${activeListAgentId}/context`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              credentials: 'include',
              body: JSON.stringify({
                aiArrivalPrompt: config.systemPrompt,
                aiScheduleInterval: config.scheduleInterval,
              }),
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error ${response.status}`);
          }
          console.log('✅ List agent config saved');
          setActiveListAgentId(null);
        } catch (error) {
          console.error('Error saving list agent config:', error);
        }
      },
      [accessToken, activeListAgentId]
    );

    const handleDragStart = useCallback(() => {
      document.body.classList.add(globalStyles.dragging);
      closePopup();
    }, []);

    const handleDragEnd = useCallback(
      ({ draggableId, type, source, destination }) => {
        document.body.classList.remove(globalStyles.dragging);

        if (
          !destination ||
          (source.droppableId === destination.droppableId && source.index === destination.index)
        ) {
          return;
        }

        const id = parseDndId(draggableId);

        switch (type) {
          case DroppableTypes.LIST:
            onListMove(id, destination.index);

            break;
          case DroppableTypes.CARD:
            onCardMove(id, parseDndId(destination.droppableId), destination.index);

            break;
          default:
        }
      },
      [onListMove, onCardMove]
    );

    const handleMouseDown = useCallback(
      event => {
        // If button is defined and not equal to 0 (left click)
        if (event.button) {
          return;
        }

        if (event.target !== wrapper.current && !event.target.dataset.dragScroller) {
          return;
        }

        prevPosition.current = event.clientX;

        window.getSelection().removeAllRanges();
        document.body.classList.add(globalStyles.dragScrolling);
      },
      [wrapper]
    );

    const handleWindowMouseMove = useCallback(
      event => {
        if (prevPosition.current === null) {
          return;
        }

        event.preventDefault();

        window.scrollBy({
          left: prevPosition.current - event.clientX,
        });

        prevPosition.current = event.clientX;
      },
      [prevPosition]
    );

    const handleWindowMouseRelease = useCallback(() => {
      if (prevPosition.current === null) {
        return;
      }

      prevPosition.current = null;
      document.body.classList.remove(globalStyles.dragScrolling);
    }, [prevPosition]);

    const handleMobileScroll = useCallback(() => {
      if (!isMobile || !wrapper.current) return;
      const el = wrapper.current;
      const listWidth = el.offsetWidth;
      const idx = Math.round(el.scrollLeft / listWidth);
      setActiveListIndex(Math.min(idx, listIds.length - 1));
    }, [isMobile, listIds.length]);

    useEffect(() => {
      if (isMobile && wrapper.current) {
        const el = wrapper.current;
        el.addEventListener('scroll', handleMobileScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleMobileScroll);
      }
      return undefined;
    }, [isMobile, handleMobileScroll]);

    useEffect(() => {
      if (isMobile) {
        document.body.style.overflowX = 'hidden';
      } else {
        document.body.style.overflowX = 'auto';
      }

      return () => {
        document.body.style.overflowX = null;
      };
    }, [isMobile]);

    useEffect(() => {
      if (isListAddOpened) {
        window.scroll(document.body.scrollWidth, 0);
      }
    }, [listIds, isListAddOpened]);

    useEffect(() => {
      window.addEventListener('mousemove', handleWindowMouseMove);

      window.addEventListener('mouseup', handleWindowMouseRelease);
      window.addEventListener('blur', handleWindowMouseRelease);
      window.addEventListener('contextmenu', handleWindowMouseRelease);

      return () => {
        window.removeEventListener('mousemove', handleWindowMouseMove);

        window.removeEventListener('mouseup', handleWindowMouseRelease);
        window.removeEventListener('blur', handleWindowMouseRelease);
        window.removeEventListener('contextmenu', handleWindowMouseRelease);
      };
    }, [handleWindowMouseMove, handleWindowMouseRelease]);

    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          ref={wrapper}
          className={styles.wrapper}
          onMouseDown={isMobile ? undefined : handleMouseDown}
        >
          <div style={isMobile ? { height: '100%' } : undefined}>
            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <Droppable droppableId="board" type={DroppableTypes.LIST} direction="horizontal">
                {({ innerRef, droppableProps, placeholder }) => (
                  <div
                    {...droppableProps} // eslint-disable-line react/jsx-props-no-spreading
                    data-drag-scroller
                    ref={innerRef}
                    className={styles.lists}
                  >
                    {listIds.map((listId, index) => (
                      <ListContainer
                        key={listId}
                        id={listId}
                        index={index}
                        onConfigureListAgent={handleConfigureListAgent}
                      />
                    ))}
                    {placeholder}
                    {canEdit && (
                      <div data-drag-scroller className={styles.list}>
                        {isListAddOpened ? (
                          <ListAdd onCreate={onListCreate} onClose={handleAddListClose} />
                        ) : (
                          <button
                            type="button"
                            className={styles.addListButton}
                            onClick={handleAddListClick}
                          >
                            <PlusMathIcon className={styles.addListButtonIcon} />
                            <span className={styles.addListButtonText}>
                              {listIds.length > 0
                                ? t('action.addAnotherList')
                                : t('action.addList')}
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
        {isMobile && listIds.length > 1 && (
          <div className={styles.mobileIndicators}>
            <div className={styles.listNameIndicator}>
              {listNames && listNames[activeListIndex]
                ? listNames[activeListIndex]
                : `List ${activeListIndex + 1}`}
            </div>
            <div className={styles.listIndicator}>
              {listIds.map((id, idx) => (
                <div
                  key={id}
                  className={classNames(
                    styles.listDot,
                    idx === activeListIndex && styles.listDotActive
                  )}
                />
              ))}
            </div>
          </div>
        )}
        {isCardModalOpened && <CardModalContainer />}
        <AgentPromptModal
          isOpen={!!activeListAgentId}
          entityType="list"
          agentType={activeAgentType}
          entityName={activeListAgentName}
          systemPrompt={aiConfig.systemPrompt}
          agentEnabled={aiConfig.agentEnabled}
          scheduleInterval={aiConfig.scheduleInterval}
          onSave={handleSaveListAgentConfig}
          onClose={() => setActiveListAgentId(null)}
        />
      </>
    );
  }
);

Board.propTypes = {
  listIds: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  listNames: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  isCardModalOpened: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  onListCreate: PropTypes.func.isRequired,
  onListMove: PropTypes.func.isRequired,
  onCardMove: PropTypes.func.isRequired,
};

Board.defaultProps = {
  listNames: [],
};

export default Board;
